import {CORE_AUTH_DOMAIN, PAY_AUTH_DOMAIN} from '~/constants/authorize';
import type {BuildAuthorizeUrlParams} from '~/types/authorizeUrlParams';
import {
  buildUrlSearchParams,
  buildCustomerAuthLoginParams,
} from '~/utils/buildUrlSearchParams';

import {isoWindow} from './window';

// The storefront must set window.Shopify.SignInWithShop.coreIdp flag to true to enable CoreIDP login
// This is necessary in order to not try core IDP login for legacy accounts
function isCoreIdpSessionEnabled() {
  return Boolean(isoWindow?.Shopify?.SignInWithShop?.coreIdp);
}

// Only allow CoreIDP login for loginWithShopSelfServe analyticsContext
// This is the best way to detect if we are logging in to the storefront
// because flow is "default" and on Horizon the flow-version will be overridden
// to 'account-actions-popover' on the liquid drop login button
//
// isCoreIdpSessionEnabled is not enough because other shop-js flows such as discount and follow
// may also be on the page and we don't want to try core IDP login for them.
function isAllowedForCoreIdp(payload: BuildAuthorizeUrlParams) {
  return payload.analyticsContext === 'loginWithShopSelfServe';
}

export function buildAuthorizeUrl(payload: BuildAuthorizeUrlParams) {
  if (!payload.proxy && payload?.clientId === undefined) {
    return '';
  }

  if (payload.proxy) {
    if (isCoreIdpSessionEnabled() && isAllowedForCoreIdp(payload)) {
      // Use special shop_params parameter handling for customer_authentication/login
      const params = buildCustomerAuthLoginParams(payload);
      return `${CORE_AUTH_DOMAIN}/customer_authentication/login?${params}`;
    } else {
      const params = buildUrlSearchParams(payload);
      return `${CORE_AUTH_DOMAIN}/services/login_with_shop/authorize?${params}`;
    }
  }

  const params = buildUrlSearchParams(payload);

  if (shouldUseNewUI(payload)) {
    return `${PAY_AUTH_DOMAIN}/oauth/authorize?${params}`;
  }

  /**
   * When `avoidPayAltDomain` is false, we need to route requests through the shop.app/pay/sdk-session endpoint.
   * The session endpoint will allow us to check for the presence of a Pay user session  and hoist it to the
   * "alt" pay.shopify.com/pay/sdk-authorize endpoint.
   */
  const path = payload.avoidPayAltDomain
    ? '/pay/sdk-authorize'
    : '/pay/sdk-session';

  return `${PAY_AUTH_DOMAIN}${path}?${params}`;
}

// For new customer accounts, if uxMode is windoid or redirect,
// deliver the new login UI, except for the PreAuthPrompt
function shouldUseNewUI(payload: BuildAuthorizeUrlParams) {
  return (
    payload.analyticsContext === 'loginWithShopSelfServe' &&
    payload.uxMode !== 'iframe' &&
    payload.uxRole !== 'prompt'
  );
}
